import { useCallback, useEffect, useRef, useState } from 'react';
import { PAGE_SIZE } from '../common';
import { api, ApiError } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import type { Booking, BookingsFilter, ListTotals, LoadState, SortState } from './types';
import type { ListRow } from '@bbbk/schema';
import { isAll, listKindOf } from './filterTypes';

// Pure: build the GET bookings query string for a filter + page + sort. Empty /
// null filter parts are omitted so the server clause switches off. Sort, when
// given, is sent to the server (sort key + dir) so ordering spans the whole
// table, not just the loaded page. Returns the path relative to the API base —
// the client owns the /api prefix. Exported for unit testing (search.test.ts).
export function buildBookingsQuery(
  filter: BookingsFilter,
  limit: number,
  offset: number,
  sort?: SortState,
): string {
  const p = new URLSearchParams();
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  const q = filter.q?.trim();
  if (q) p.set('q', q);
  if (filter.status) p.set('status', filter.status);
  if (filter.area) p.set('area', filter.area);
  if (filter.inOn) p.set('inOn', filter.inOn);
  if (sort) {
    p.set('sort', sort.key);
    p.set('dir', sort.dir);
  }
  return `/bookings?${p.toString()}`;
}

// The mixed list's query (architecture api.md): `types` joined, a status —
// under All it belongs to one kind and the route narrows to that side — the
// window as from/to, never inOn, which is a bookings-only occupancy question.
// Exported for unit testing (search.test.ts).
export function buildMixedQuery(
  filter: BookingsFilter,
  limit: number,
  offset: number,
  sort?: SortState,
): string {
  const p = new URLSearchParams();
  p.set('types', isAll(filter.types) ? 'booking,viewing' : filter.types.join(','));
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  const q = filter.q?.trim();
  if (q) p.set('q', q);
  if (filter.status) p.set('status', filter.status);
  if (filter.area) p.set('area', filter.area);
  if (filter.window) {
    p.set('from', filter.window.from);
    p.set('to', filter.window.to);
  }
  if (sort) {
    p.set('sort', sort.key);
    p.set('dir', sort.dir);
  }
  return `/bookings/mixed?${p.toString()}`;
}

// WHICH READ THE PAGE MAKES (access-patterns, "what the three Type states
// read"): Booking alone is the existing list, untouched — byte-identical to
// today, which is AC6's structural half; All and Viewing are the mixed list.
export function listQueryFor(filter: BookingsFilter, limit: number, offset: number, sort?: SortState): string {
  return listKindOf(filter.types) === 'booking'
    ? buildBookingsQuery(filter, limit, offset, sort)
    : buildMixedQuery(filter, limit, offset, sort);
}

export interface UseBookingsResult {
  state: LoadState;
  // The two figures, never summed (AC12). Under Booking alone the list's own
  // total lands in `bookings` and `viewings` is null.
  totals: ListTotals | null;
  page: number; // 1-based
  setPage: (page: number) => void;
  reload: () => void;
}

// The envelope either route returns: the list's `{ data: Booking[], page: { total } }`
// or the mixed list's `{ data: ListRow[], page: { totals } }`.
interface ListEnvelope {
  data?: (Booking | ListRow)[];
  page?: { total?: number | null; totals?: ListTotals | null };
}

function toRows(data: (Booking | ListRow)[]): ListRow[] {
  return data.map((d) => ('kind' in d ? d : { kind: 'booking', booking: d }));
}

// The table's data hook — server-side search/filter + standard 20-per-page
// pagination (shared Pagination component). `q` commits on Enter (no debounce).
// Each page is a fresh fetch at offset (page-1)*PAGE_SIZE; filter changes reset
// to page 1. `total` is returned by the API on the first page and cached across
// pages (the count(*) runs once per result set, not per page). A request-gen
// guard discards responses from a superseded page/filter.
// After a write, the user's NEXT reads (a search, a status-filter switch, a page
// nav) must reflect their own change — but those are plain cacheable fetches, so
// the browser can serve a stale pre-write response (the bug: created a booking,
// searched, didn't see it until refresh). For a window after a write, bypass the
// browser cache on every read so any view the user opens is fresh. The window
// covers the bookings list max-age (read-cache-strategy AP1); once it passes the
// cache entries have expired anyway and caching resumes.
const POST_WRITE_FRESH_MS = 30_000;

export function useBookings(filter: BookingsFilter, sort: SortState): UseBookingsResult {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [totals, setTotals] = useState<ListTotals | null>(null);
  const [page, setPage] = useState(1);
  // The shared SWR trigger core: reload() nonce, activation+mutation tick,
  // fetchPlan, and the generation guard all live in lib/useFetchTrigger. A
  // reload (post-write) bypasses the HTTP cache once so the user's own write
  // shows fresh (read-cache-strategy AC3/AC4); an on-return / mutation-signal
  // revalidation is silent — content holds, no loading flash (AC3/AC4). A plain
  // page/filter change re-reads cacheable.
  const trigger = useFetchTrigger();
  // Timestamp until which every read bypasses the browser cache — set on a write
  // so the user's own change is visible across subsequent searches/filters.
  const freshUntilRef = useRef(0);

  // Reset to page 1 whenever the filter or sort changes (only if not already
  // there, to avoid a redundant fetch).
  // THE THREE LISTS MOVE TOGETHER (frame.md, landmine 1): this reset, the fetch
  // deps below, and EMPTY_BOOKINGS_FILTER — a filter field missing from one is a
  // page that neither refetches nor resets.
  const typesKey = filter.types.join(',');
  const windowKey = filter.window ? `${filter.window.from}..${filter.window.to}` : '';
  useEffect(() => {
    setPage((p) => (p === 1 ? p : 1));
  }, [filter.q, filter.status, filter.area, filter.inOn, typesKey, windowKey, sort.key, sort.dir]);

  useEffect(() => {
    const run = trigger.begin();
    // A silent on-return revalidation holds the last-known content on screen —
    // no drop to loading, no blank-then-populate (AC3). Every other trigger
    // shows the loading state as before.
    if (run.plan.showLoading) setState({ phase: 'loading' });
    // The client owns base path, CSRF, 401-redirect, and envelope unwrap; the
    // trigger core owns the generation guard. A reload- or revalidate-driven
    // refetch bypasses the browser cache so a change made elsewhere is caught
    // (AC4); so does any read inside the post-write freshness window, so the
    // user's own create/edit shows up in their next search or filter switch.
    const bypassCache = run.plan.bypassCache || Date.now() < freshUntilRef.current;
    api<(Booking | ListRow)[]>(listQueryFor(filter, PAGE_SIZE, (page - 1) * PAGE_SIZE, sort), bypassCache ? { cache: 'reload' } : {})
      .then((body: ListEnvelope) => {
        if (!run.isCurrent()) return; // superseded by a newer page/filter/return
        const data = body.data ?? [];
        // The figures come back on the first page (offset 0); cache them across
        // pages. The list route's single `total` is the bookings figure.
        if (body.page?.totals != null) setTotals(body.page.totals);
        else if (body.page?.total != null) setTotals({ bookings: body.page.total, viewings: null });
        run.markContent();
        setState({ phase: 'ready', rows: toRows(data) });
      })
      .catch((e: unknown) => {
        // A 401 has already redirected inside the client; nothing left to show.
        if (e instanceof ApiError && e.status === 401) return;
        if (run.isCurrent()) {
          // A failed silent revalidation must not clobber the content on screen:
          // the orphaned/failed background read leaves the last-known view in
          // place. Only a foreground fetch surfaces the error state.
          if (run.isRevalidate && run.hasContent) return;
          setState({ phase: 'error', message: e instanceof Error ? e.message || undefined : undefined });
        }
      });
    return run.cancel;
  }, [filter.q, filter.status, filter.area, filter.inOn, typesKey, windowKey, sort.key, sort.dir, page, trigger.nonce, trigger.tick]);

  const reload = useCallback(() => {
    // Open the freshness window so the next reads (incl. searches) bypass the
    // stale browser cache, then trigger the immediate post-write refetch.
    freshUntilRef.current = Date.now() + POST_WRITE_FRESH_MS;
    trigger.reload();
  }, [trigger.reload]);

  return { state, totals, page, setPage, reload };
}
